<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';
	import AdminUserStore from '@/Stores/AdminUserStore.js';
	// Services
	import { getUrlParameter, setUrlParameter, paginationBuilder } from '@/Services/Helpers.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Helmet from '@/Components/Helmet.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	// Partials
	import CreateAdminUser from '@/Partials/AdminUser/_Create.svelte';
	// Constants
	import { TwoFactorTypes } from '@/Constants/User.js';
	import { AdminUserStatus } from '@/Constants/AdminUser.js';

	$: links =
		$AdminUserStore &&
		!$AdminUserStore.id &&
		$AdminUserStore.id !== null &&
		paginationBuilder(AdminUserStore.getPagination());

	let pageNo = getUrlParameter('page') || 1;
	let loading = getAdminUsers();

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			getAdminUsers();
			setUrlParameter('page', pageNo);
		}
	}

	function getAdminUsers() {
		return AdminUserStore.getAdminUsers({ size: 50, number: pageNo });
	}

	function click(id) {
		navigate(`/admin-users/${id}`);
	}

	let showCreateAdminUser = false;
</script>

<Helmet title="Admin users" />

{#if showCreateAdminUser}
	<CreateAdminUser
		bind:open={showCreateAdminUser}
		on:created={() => {
			showCreateAdminUser = false;
			getAdminUsers();
		}}
	/>
{/if}

<div>
	{#await loading}
		<Loading />
	{:then}
		<div>
			<!-- Page Header -->
			<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
				<div class="flex items-center justify-between w-full">
					<h1 class="text-xl font-bold">Admin Users</h1>

					<div class="w-56 text-right">
						{#if $ACLStore && ACLStore.granted('admin_users.create')}
							<button on:click={() => (showCreateAdminUser = true)} class="btn btn-primary"> Create Admin User </button>
						{/if}
					</div>
				</div>
			</div>
			<!-- /Page Header -->
			<div class="p-2 mx-4 bg-gray-100 rounded">
				<Pagination {links} on:click={changePage} className="mt-2" />
				<div class="overflow-x-auto rounded">
					<table class="w-full whitespace-nowrap">
						<thead>
							<tr class="text-xs text-left text-gray-500 uppercase">
								<th class="w-4 px-1 pt-5 pb-4 font-medium">Super User</th>
								<th class="px-6 pt-5 pb-4 font-medium">Username</th>
								<th class="px-6 pt-5 pb-4 font-medium">First</th>
								<th class="px-6 pt-5 pb-4 font-medium">Last</th>
								<th class="px-6 pt-5 pb-4 font-medium">Email</th>
								<th class="px-6 pt-5 pb-4 font-medium">Role</th>
								<th class="px-6 pt-5 pb-4 font-medium">2FA</th>
								<th class="px-6 pt-5 pb-4 font-medium">Status</th>
								<th class="px-6 pt-5 pb-4 font-medium" />
							</tr>
						</thead>
						<tbody class="bg-white">
							{#each $AdminUserStore.all() as adminUser (adminUser.id)}
								<tr
									on:click={() => click(adminUser.id)}
									class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
								>
									<td
										class="p-4 px-1 text-center border-t {adminUser.is_super_user ? 'text-green-500' : 'text-gray-500'}"
									>
										<Icon name={adminUser.is_super_user ? 'circle-tick' : 'circle-x'} classes="mx-auto" />
									</td>
									<td class="p-4 px-6 font-semibold border-t">{adminUser.name}</td>
									<td class="p-4 px-6 border-t">{adminUser.first_name}</td>
									<td class="p-4 px-6 border-t">{adminUser.last_name}</td>
									<td class="p-4 px-6 border-t">{adminUser.email}</td>
									<td class="p-4 px-6 border-t">
										{#if adminUser.is_super_user}
											Super User
										{:else}
											{(adminUser.roles && adminUser.roles.pluck('name').first()) || ''}
										{/if}
										{#if adminUser.is_account_manager}
											<div class="-mt-1">
												<span class="px-1 text-white bg-purple-500 rounded-md text-tiny"> Account Manager </span>
											</div>
										{/if}
									</td>
									<td class="p-4 px-6 border-t">
										{#if adminUser.two_factor_type === TwoFactorTypes.SMS}
											<Icon name="sms" classes="block w-6 h-6 text-green-500 inline" />
										{:else if adminUser.two_factor_type === TwoFactorTypes.Email}
											<Icon name="email" classes="block w-6 h-6 text-green-500 inline" />
										{:else if adminUser.two_factor_type === TwoFactorTypes.TOTP}
											<Icon name="smartphone" classes="block w-6 h-6 text-green-500 inline" />
										{:else}
											<Icon name="circle-x" classes="block w-6 h-6 text-gray-500 inline" />
										{/if}
									</td>
									<td class="p-4 px-6 border-t">
										{#if adminUser.status_id === AdminUserStatus.Active}
											<Icon name="tick" classes="block w-6 h-6 text-green-500 inline" />
										{:else}
											<Icon name="circle-x" classes="block w-6 h-6 text-gray-500 inline" />
										{/if}
									</td>
									<td class="w-px px-6 border-t">
										<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
									</td>
								</tr>
							{:else}
								<tr>
									<td class="px-6 py-4 text-center border-t" colspan="2"> No admin users found. </td>
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
				<Pagination {links} on:click={changePage} className="my-3" />
			</div>
		</div>
	{/await}
</div>
